import {
  Box,
  Card,
  Divider,
  FormHelperText,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import InputControl from "../../../shared/components/InputControl";
import { useLocation, useParams } from "react-router-dom";
import { Loader } from "../../../shared";
import { LoadingButton } from "@mui/lab";
import { useManageAccountFileUpload } from "../hooks";
import { FileDropzoneComponent } from "./FileDropzoneComponent";

export const ManageAccountFileUpload = () => {
  const { id } = useParams();
  const location = useLocation();
  const isStoreAdmin = location.pathname.includes("/store-admin");

  const {
    state,
    storeList,
    sample_import_file,
    pl_bs_accounts_months,
    pl_bs_accounts_years,
    formValidator,
    handleFormChange,
    handleFileChange,
    onUploadAccountFile,
  } = useManageAccountFileUpload();

  const isUpdatePage = Boolean(id);

  const helperText = {
    store: formValidator.current.message(
      "Store",
      state.data.store_id,
      "required"
    ),
    month: formValidator.current.message(
      "Month",
      state?.data?.month,
      "required"
    ),
    year: formValidator.current.message("Year", state?.data?.year, "required"),
    file: state.file
      ? formValidator.current.message(
          "Excel File",
          state?.file,
          "required|IconFormat"
        )
      : formValidator.current.message(
          "Excel File",
          state?.data?.excel_file,
          "required|IconFormat"
        ),
  };

  return (
    <Card sx={{ p: "32px 24px", mb: "24px" }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Typography variant="subtitle1" fontWeight={600}>
            Account File Upload
          </Typography>
        </Grid>
        <Grid item sm={8}>
          {state.uploadsFormLoading ? (
            <Stack
              height="370px"
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
            >
              <Loader />
            </Stack>
          ) : (
            <Stack width={"100%"} gap="20px">
              <Box
                component="a"
                href={sample_import_file}
                sx={{
                  color: "inherit",
                  fontSize: "12px",
                  float: "right",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  fontWeight: 400,
                  ml: "auto",
                }}
              >
                P&L and BS Template
                <Box
                  component="img"
                  src="/icons/simple_download_ic.png"
                  alt="Pl&BS Template"
                />
              </Box>

              {!isStoreAdmin && (
                <InputControl
                  type="dropdown"
                  disableClearable
                  options={storeList ?? []}
                  getOptionLabel={(option) => option.name}
                  value={
                    (storeList?.length > 0 &&
                      state?.data?.store_id &&
                      storeList?.find((c) => c.id === state?.data?.store_id)) ||
                    null
                  }
                  onChange={(e, newValue) => {
                    handleFormChange({
                      target: {
                        name: "store_id",
                        value: newValue.id,
                      },
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Store"
                      required
                      error={Boolean(helperText.store)}
                      helperText={helperText.store}
                    />
                  )}
                  disabled={isUpdatePage}
                />
              )}
              <Stack direction="row" gap={1}>
                <InputControl
                  type="dropdown"
                  options={pl_bs_accounts_months ?? []}
                  getOptionLabel={(option) => option?.label}
                  value={
                    (pl_bs_accounts_months?.length > 0 &&
                      state?.data?.month &&
                      pl_bs_accounts_months?.find(
                        (c) => c.label === state?.data?.month
                      )) ||
                    null
                  }
                  onChange={(e, newValue) => {
                    handleFormChange({
                      target: {
                        name: "month",
                        value: newValue?.label,
                      },
                    });
                  }}
                  sx={{
                    width: "100%",
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Month"
                      required
                      error={Boolean(helperText.month)}
                      helperText={helperText.month}
                    />
                  )}
                  disabled={Boolean(id)}
                />
                <InputControl
                  type="dropdown"
                  disableClearable={true}
                  options={pl_bs_accounts_years ?? []}
                  getOptionLabel={(option) => option.label}
                  value={
                    (pl_bs_accounts_years?.length > 0 &&
                      state?.data?.year &&
                      pl_bs_accounts_years?.find(
                        (c) => c.value === state?.data?.year
                      )) ||
                    null
                  }
                  onChange={(e, newValue) => {
                    handleFormChange({
                      target: {
                        name: "year",
                        value: newValue.value,
                      },
                    });
                  }}
                  sx={{
                    width: "100%",
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Year"
                      required
                      error={Boolean(helperText.year)}
                      helperText={helperText.year}
                    />
                  )}
                  disabled={Boolean(id)}
                />
              </Stack>
              <Typography variant="subtitle1" gutterBottom>
                File*
              </Typography>
              <FileDropzoneComponent
                handleDropzone={(file) => handleFileChange(file)}
                fileName={state?.file?.name || state?.data?.excel_file}
              />
              <FormHelperText sx={{ color: "#C60808" }}>
                {helperText.file}
              </FormHelperText>
            </Stack>
          )}
        </Grid>
      </Grid>
      <Stack mt={3}>
        <Divider />
      </Stack>
      <Stack direction="row" justifyContent="right" mt={2}>
        <LoadingButton
          variant="contained"
          onClick={onUploadAccountFile}
          disabled={isUpdatePage ? state.isFormUpdated : false}
          loading={state.uploadButtonLoading}
        >
          Upload
        </LoadingButton>
      </Stack>
    </Card>
  );
};
